import { useCallback, useState } from 'react';

/**
 * Encapsulates EditCourse left/right panel UI state and toggling behavior.
 *
 */
export function usePanelState({ breakpoints, menuEditLeftMode, setMenuEditLeftMode, PANEL_MODES }) {
  const [isLeftPanelCompact, setIsLeftPanelCompact] = useState(false);
  const [isRightPanelOpen, setIsRightPanelOpen] = useState(false);

  const isMobileScreen = useCallback(() => window.innerWidth < breakpoints.tablet, [breakpoints]);

  const isMediumScreen = useCallback(() => window.innerWidth >= breakpoints.tablet && window.innerWidth < breakpoints.bigTablet, [breakpoints]);

  const toggleLeftPanel = useCallback(() => {
    const medium = isMediumScreen();

    setIsLeftPanelCompact((prev) => {
      const newValue = !prev;

      // On medium screens: if expanding left panel, close right panel
      if (medium && !newValue) {
        setIsRightPanelOpen(false);
      }

      return newValue;
    });
  }, [isMediumScreen]);

  const toggleLeftPanelMode = useCallback(() => {
    const newMode = menuEditLeftMode === PANEL_MODES.TOC ? PANEL_MODES.STRUCTURE : PANEL_MODES.TOC;
    setMenuEditLeftMode(newMode);

    if (isLeftPanelCompact) {
      setIsLeftPanelCompact(false);
    }
  }, [PANEL_MODES, isLeftPanelCompact, menuEditLeftMode, setMenuEditLeftMode]);

  const toggleRightPanel = useCallback(
    (valueOrEvent) => {
      const medium = isMediumScreen();
      const isEvent = valueOrEvent && typeof valueOrEvent === 'object' && 'timeStamp' in valueOrEvent;

      if (isEvent || valueOrEvent === undefined) {
        setIsRightPanelOpen((prev) => {
          const newValue = !prev;

          // On medium screens: if opening right panel, compact left panel
          if (medium && newValue) {
            setIsLeftPanelCompact(true);
          }

          return newValue;
        });
      } else {
        setIsRightPanelOpen(valueOrEvent);

        // On medium screens: if opening right panel, compact left panel
        if (medium && valueOrEvent) {
          setIsLeftPanelCompact(true);
        }
      }
    },
    [isMediumScreen]
  );

  const compactLeftPanel = useCallback(() => {
    setIsLeftPanelCompact(true);
  }, []);

  const openRightPanel = useCallback(() => {
    const medium = isMediumScreen();
    setIsRightPanelOpen(true);

    // On medium screens: opening right panel compacts left panel
    if (medium) {
      setIsLeftPanelCompact(true);
    }
  }, [isMediumScreen]);

  const closeRightPanel = useCallback(() => {
    setIsRightPanelOpen(false);
  }, []);

  return {
    isLeftPanelCompact,
    setIsLeftPanelCompact,
    isRightPanelOpen,
    setIsRightPanelOpen,
    toggleLeftPanel,
    toggleLeftPanelMode,
    toggleRightPanel,
    compactLeftPanel,
    openRightPanel,
    closeRightPanel,
    isMobileScreen,
    isMediumScreen,
  };
}
